import { useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ChecklistRounded from "@mui/icons-material/ChecklistRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { canExecute } from "../model/canExecute";
import {
  dangerReasonLines,
  PLAN_STATUS_KEYS,
  STEP_STATUS_KEYS,
} from "../model/labels";
import { isHttpUrl } from "../model/safeUrl";
import type { PlanDto, PlanStatus, PlanStepDto } from "../model/types";
import { EntityCardView } from "./EntityCardView";

const STATUS_COLORS: Record<
  PlanStatus,
  "default" | "info" | "success" | "warning" | "error"
> = {
  DRAFT: "info",
  RUNNING: "info",
  DONE: "success",
  PARTIAL: "warning",
  FAILED: "error",
  CANCELLED: "default",
};

const SectionCaption: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    component="div"
    sx={{
      textTransform: "uppercase",
      letterSpacing: 0.4,
      fontWeight: 600,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const StepMarker: FC<{ step: PlanStepDto; active: boolean }> = ({
  step,
  active,
}) => {
  const box = {
    width: 22,
    height: 22,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as const;
  if (step.status === "DONE") {
    return (
      <Box sx={box}>
        <CheckCircleRounded color="success" sx={{ fontSize: 20 }} />
      </Box>
    );
  }
  if (step.status === "FAILED") {
    return (
      <Box sx={box}>
        <ErrorRounded color="error" sx={{ fontSize: 20 }} />
      </Box>
    );
  }
  if (step.status === "SKIPPED") {
    return (
      <Box sx={box}>
        <RemoveCircleOutlineRounded
          sx={{ fontSize: 20, color: "text.disabled" }}
        />
      </Box>
    );
  }
  if (active) {
    return (
      <Box sx={box}>
        <CircularProgress size={16} thickness={5} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        ...box,
        borderRadius: "50%",
        border: 1.5,
        borderColor: step.dangerous ? "error.main" : "divider",
        color: step.dangerous ? "error.main" : "text.secondary",
        fontSize: "0.6875rem",
        fontWeight: 600,
      }}
    >
      {step.index + 1}
    </Box>
  );
};

/**
 * Карточка плана (п. 4 спеки): предпросмотр, шаги, опасные шаги с
 * отдельным подтверждением, одна кнопка «Сделать» и «Отменить».
 */
export const PlanCard: FC<{
  plan: PlanDto;
  busy: boolean;
  onExecute: (planId: string, confirmedSteps: number[]) => void;
  onCancel: (planId: string) => Promise<unknown> | undefined;
}> = ({ plan, busy, onExecute, onCancel }) => {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState<number[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const locked = busy || cancelling;
  const draft = plan.status === "DRAFT";
  const running = plan.status === "RUNNING";
  const ready = canExecute(plan, confirmed);
  const hasDangerous = plan.steps.some((s) => s.dangerous);
  const activeIndex = running
    ? plan.steps.find((s) => s.status === "PENDING")?.index
    : undefined;
  const sources = plan.sources.filter((url) => isHttpUrl(url));

  const toggle = (index: number, checked: boolean) =>
    setConfirmed((prev) =>
      checked
        ? prev.includes(index)
          ? prev
          : [...prev, index]
        : prev.filter((i) => i !== index)
    );

  return (
    <Card variant="outlined" sx={{ my: 1, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: "action.hover",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <ChecklistRounded color="primary" sx={{ fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
          {plan.title}
        </Typography>
        <Chip
          size="small"
          color={STATUS_COLORS[plan.status]}
          variant={draft ? "outlined" : "filled"}
          label={t(PLAN_STATUS_KEYS[plan.status])}
          sx={{ flexShrink: 0 }}
        />
      </Stack>
      {running && <LinearProgress sx={{ height: 2 }} />}

      <Stack gap={1.75} sx={{ p: 1.5 }}>
        {plan.previews.length > 0 && (
          <Box>
            <SectionCaption>
              {t("partnerAssistant.plan.preview")}
            </SectionCaption>
            <Stack gap={1}>
              {plan.previews.map((card, i) => (
                <EntityCardView key={`${card.entityType}-${i}`} card={card} />
              ))}
            </Stack>
          </Box>
        )}

        <Stack component="ol" gap={1} sx={{ m: 0, p: 0, listStyle: "none" }}>
          {plan.steps.map((step) => {
            const checked = confirmed.includes(step.index);
            return (
              <Box component="li" key={step.index}>
                <Stack direction="row" gap={1.25} alignItems="flex-start">
                  <StepMarker step={step} active={step.index === activeIndex} />
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                    <Stack direction="row" gap={1} alignItems="baseline">
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color:
                            step.status === "SKIPPED"
                              ? "text.disabled"
                              : "text.primary",
                        }}
                      >
                        {step.description}
                      </Typography>
                      {step.status !== "PENDING" && (
                        <Typography
                          variant="caption"
                          color={
                            step.status === "FAILED"
                              ? "error"
                              : "text.secondary"
                          }
                          sx={{ flexShrink: 0 }}
                        >
                          {t(STEP_STATUS_KEYS[step.status])}
                        </Typography>
                      )}
                    </Stack>
                    {step.dangerous && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        columnGap={1}
                        sx={{
                          mt: 0.75,
                          pl: 1,
                          pr: draft ? 0.5 : 1,
                          py: 0.5,
                          borderRadius: 1,
                          border: 1,
                          borderColor: (theme) =>
                            alpha(theme.palette.error.main, 0.4),
                          bgcolor: (theme) =>
                            alpha(theme.palette.error.main, 0.06),
                          color: "error.main",
                        }}
                      >
                        <WarningAmberRounded
                          sx={{
                            fontSize: 18,
                            alignSelf: "flex-start",
                            mt: 0.25,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{ fontWeight: 600 }}
                          >
                            {t("partnerAssistant.plan.dangerous")}
                          </Typography>
                          {dangerReasonLines(step, t).map((line) => (
                            <Typography
                              key={line}
                              variant="caption"
                              component="div"
                              sx={{ wordBreak: "break-word", opacity: 0.85 }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                        {draft && (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                color="error"
                                checked={checked}
                                disabled={locked}
                                onChange={(e) =>
                                  toggle(step.index, e.target.checked)
                                }
                              />
                            }
                            label={
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                {t("partnerAssistant.plan.confirmDangerous")}
                              </Typography>
                            }
                          />
                        )}
                      </Stack>
                    )}
                    {step.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        component="div"
                        sx={{ mt: 0.25, wordBreak: "break-word" }}
                      >
                        {step.error}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {plan.notes.length > 0 && (
          <Stack
            direction="row"
            gap={1}
            sx={{
              p: 1.25,
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            }}
          >
            <InfoOutlined color="info" sx={{ fontSize: 18, mt: 0.25 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("partnerAssistant.plan.notes")}
              </Typography>
              <Box component="ul" sx={{ pl: 2.25, my: 0.25 }}>
                {plan.notes.map((note, i) => (
                  <Typography key={i} component="li" variant="body2">
                    {note}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Stack>
        )}

        {sources.length > 0 && (
          <Box>
            <SectionCaption>
              {t("partnerAssistant.plan.sources")}
            </SectionCaption>
            {sources.map((url) => (
              <Typography key={url} variant="body2" noWrap>
                <Link
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {url}
                </Link>
              </Typography>
            ))}
          </Box>
        )}
      </Stack>

      {draft && (
        <>
          <Divider />
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ px: 1.5, py: 1.25 }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={locked || !ready}
              onClick={() => onExecute(plan.id, confirmed)}
            >
              {t("partnerAssistant.plan.do")}
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={locked}
              onClick={() => {
                setCancelling(true);
                void Promise.resolve(onCancel(plan.id)).finally(() =>
                  setCancelling(false)
                );
              }}
              sx={{ color: "text.secondary" }}
            >
              {t("partnerAssistant.plan.cancel")}
            </Button>
            {hasDangerous && !ready && (
              <Typography variant="caption" color="text.secondary">
                {t("partnerAssistant.plan.confirmHint")}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Card>
  );
};
